import React from "react";
import { graphql } from "gatsby";
import { motion } from "framer-motion";
import { GatsbyImage } from "gatsby-plugin-image";
import ArrowSvg from "../../images/arrow.svg";
import IconMedium from "../../images/icon-medium.svg";
import NowNextLogo from "../../images/NowNextLogo.svg";
import {
  combine,
  FADE_LEFT,
  FADE_RIGHT,
  FADE_UP,
  HOVER_SHADOW,
  STAGGER,
  useInView,
} from "../animationUtils";
import PrismicLink from "../prismicLink";
import Subscribe from "../subscribe";
import { slugify } from "../../utils/utils";

export const fragments = graphql`
  fragment MediumPost on PrismicMediumPost {
    uid
    type
    tags
    _previewable
    data {
      title {
        text
      }
      featured_image {
        alt
        gatsbyImageData(layout: CONSTRAINED, height: 460)
      }
      medium_link {
        url
      }
      team_member {
        document {
          ... on PrismicTeamMember {
            _previewable
            data {
              title {
                text
              }
              featured_image {
                alt
                gatsbyImageData(layout: CONSTRAINED, width: 100)
              }
              short_bio {
                text
              }
              position {
                text
              }
            }
          }
        }
      }
    }
  }
  fragment Homepage_Medium on PrismicHomepageDataBodyMedium {
    id
    items {
      medium_posts {
        document {
          ...MediumPost
        }
      }
    }
    primary {
      medium_title {
        text
      }
      show_featured_image_on_grid
    }
  }
  fragment FocusArea_Medium on PrismicFocusAreaDataBodyMediumPosts {
    id
    items {
      medium_posts {
        document {
          ...MediumPost
        }
      }
    }
    primary {
      medium_title {
        text
      }
      show_featured_image_on_grid
    }
  }
  fragment Page_Medium on PrismicPageDataBodyMediumPosts {
    id
    items {
      medium_posts {
        document {
          ...MediumPost
        }
      }
    }
    primary {
      medium_title {
        text
      }
      show_featured_image_on_grid
    }
  }
`;

const getAuthorData = (post) => {
  const authorImage = post?.data?.team_member?.document?.data?.featured_image;
  const authorName = post?.data?.team_member?.document?.data?.title?.text;
  const authorPosition =
    post?.data?.team_member?.document?.data?.position?.text;

  return {
    authorImage,
    authorName,
    authorPosition,
  };
};

export const PostFeatured = ({ post, hideCat = false }) => {
  const author = getAuthorData(post);
  return (
    <div className="blog-posts__featured">
      <motion.div
        {...combine(FADE_LEFT, HOVER_SHADOW)}
        animate="start"
        className="blog-posts__text"
      >
        <p className="blog-posts__category">
          {!hideCat && post?.tags?.[0] !== undefined ? (
            <a href={`/blog/category/${slugify(post?.tags?.[0])}/`}>
              {post?.tags?.[0]}
            </a>
          ) : (
            ""
          )}
        </p>
        <PrismicLink link={post} className="blog-posts__link-post">
          <h2 className="h3">{post?.data?.title?.text}</h2>
          <div className="blog-posts__author">
            <div className="blog-posts__avatar">
              {author?.authorImage && (
                <GatsbyImage
                  image={author?.authorImage?.gatsbyImageData}
                  alt={
                    author?.authorImage?.alt
                      ? author?.authorImage?.alt
                      : author?.authorName
                  }
                />
              )}
            </div>
            {author?.authorName && (
              <div>
                <span className="blog-posts__name">{author?.authorName}</span>
                {author?.authorName.trim() !==
                  author?.authorPosition.trim() && (
                  <span className="blog-posts__position">
                    {author?.authorPosition}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="blog-posts__link">
            <IconMedium />
            <ArrowSvg />
          </div>
        </PrismicLink>
      </motion.div>
      <motion.div {...FADE_RIGHT} className="blog-posts__img">
        <PrismicLink link={post}>
          {post?.data?.featured_image && (
            <GatsbyImage
              image={post?.data?.featured_image?.gatsbyImageData}
              alt={
                post?.data?.featured_image?.alt
                  ? post?.data?.featured_image?.alt
                  : ""
              }
            />
          )}
        </PrismicLink>
      </motion.div>
    </div>
  );
};

export const PostCol = ({ delay, post, hideCat = false, showFeaturedImage }) => {
  const author = getAuthorData(post);
  return (
    <motion.div
      {...combine(FADE_UP, {
        variants: {
          start: {
            transition: { delay },
          },
        },
      })}
      animate="start"
      className="blog-posts__col"
    >
      {
        showFeaturedImage &&
        <PrismicLink link={post} className="blog-posts__image">
          {post?.data?.featured_image && (
            <GatsbyImage
              image={post?.data?.featured_image?.gatsbyImageData}
              alt={
                post?.data?.featured_image?.alt
                  ? post?.data?.featured_image?.alt
                  : post?.data?.title?.text
              }
            />
          )}
        </PrismicLink>
      }
      <p className="blog-posts__category">
        {!hideCat && post?.tags?.[0] !== undefined ? (
          <a href={`/blog/category/${slugify(post?.tags?.[0])}/`}>
            {post?.tags?.[0]}
          </a>
        ) : (
          ""
        )}
      </p>
      <PrismicLink link={post} className="blog-posts__link-post">
        <h2 className="h9">{post?.data?.title?.text}</h2>
        <div className="blog-posts__author">
          <div className="blog-posts__avatar">
            {author?.authorImage && (
              <GatsbyImage
                image={author?.authorImage?.gatsbyImageData}
                alt={
                  author?.authorImage?.alt
                    ? author?.authorImage?.alt
                    : author?.authorName
                }
              />
            )}
          </div>
          {author?.authorName && (
            <div>
              <span className="blog-posts__name">{author?.authorName}</span>
              {author?.authorName.trim() !== author?.authorPosition.trim() && (
                <span className="blog-posts__position">
                  {author?.authorPosition}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="blog-posts__link">
          <IconMedium />
          <ArrowSvg />
        </div>
      </PrismicLink>
    </motion.div>
  );
};

export const PostHighlight = ({ post, className = "", hideCat = false }) => {
  const author = getAuthorData(post);
  const [ref, inView] = useInView(0.1);
  return (
    <div className={`blog-posts__featured ${className}`}>
      <motion.div
        ref={ref}
        {...combine(FADE_RIGHT, HOVER_SHADOW)}
        {...(inView ? { animate: "start" } : {})}
        className="blog-posts__text"
      >
        <p className="blog-posts__category">
          {!hideCat && post?.tags?.[0] !== undefined ? (
            <a href={`/blog/category/${slugify(post?.tags?.[0])}/`}>
              {post?.tags?.[0]}
            </a>
          ) : (
            ""
          )}
        </p>
        <PrismicLink link={post} className="blog-posts__link-post">
          <h2 className="h3">{post?.data?.title?.text}</h2>
          <div className="blog-posts__author">
            <div className="blog-posts__avatar">
              {author?.authorImage && (
                <GatsbyImage
                  image={author?.authorImage?.gatsbyImageData}
                  alt={
                    author?.authorImage?.alt
                      ? author?.authorImage?.alt
                      : author?.authorName
                  }
                />
              )}
            </div>
            {author?.authorName && (
              <div>
                <span className="blog-posts__name">{author?.authorName}</span>
                {author?.authorName.trim() !==
                  author?.authorPosition.trim() && (
                  <span className="blog-posts__position">
                    {author?.authorPosition}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="blog-posts__link">
            <IconMedium />
            <ArrowSvg />
          </div>
        </PrismicLink>
      </motion.div>
      <motion.div {...FADE_LEFT} className="blog-posts__img">
        <PrismicLink link={post}>
          {post?.data?.featured_image && (
            <GatsbyImage
              image={post?.data?.featured_image?.gatsbyImageData}
              alt={
                post?.data?.featured_image?.alt
                  ? post?.data?.featured_image?.alt
                  : post?.data?.title?.text
              }
            />
          )}
        </PrismicLink>
      </motion.div>
    </div>
  );
};

function getNodeData(data) {
  return data?.node ? data?.node : data?.medium_posts?.document;
}

function BlogPosts({ data, title, showFeaturedImage}) {
  const [ref] = useInView();
  const titleText = title ? title : data?.primary?.medium_title?.text;

  const numberFeaturedItems = 4;
  const numberDisplayItems = 4;
  let items = data?.items ? data?.items : data?.edges;
  items.length =
    items?.length > numberDisplayItems ? numberDisplayItems : items?.length;
  const itemsFeatured = items.slice(0, numberFeaturedItems);
  const itemsRemaining = items
    .slice(numberFeaturedItems, items.length)
    .reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / 3);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [];
      }

      resultArray[chunkIndex].push(item);

      return resultArray;
    }, []);

  return (
    <motion.div ref={ref} {...STAGGER} className="blog-posts">
      <div>
        <NowNextLogo className="blog-posts__modex" />
        <p className="blog-posts__title">{titleText}</p>
      </div>

      {itemsFeatured[0] && (
        <div className="blog-posts__row">
          <PostFeatured
            key={itemsFeatured[0]?.index}
            post={getNodeData(itemsFeatured[0])}
          />
        </div>
      )}
      <div className="blog-posts__row">
        {itemsFeatured[1] && (
          <PostCol
            key={itemsFeatured[1]?.index}
            post={getNodeData(itemsFeatured[1])}
            showFeaturedImage={showFeaturedImage}
          />
        )}
        {itemsFeatured[2] && (
          <PostCol
            key={itemsFeatured[2]?.index}
            post={getNodeData(itemsFeatured[2])}
            showFeaturedImage={showFeaturedImage}
          />
        )}
        {itemsFeatured[3] && (
          <PostCol
            key={itemsFeatured[3]?.index}
            post={getNodeData(itemsFeatured[3])}
            showFeaturedImage={showFeaturedImage}
          />
        )}
      </div>
      {itemsFeatured[4] && (
        <div className="blog-posts__row">
          <PostHighlight
            className={"blog-posts__highlight"}
            key={itemsFeatured[4]?.index}
            post={getNodeData(itemsFeatured[4])}
          />
        </div>
      )}

      {itemsRemaining.map((group) => {
        return (
          <>
            <div className="blog-posts__row">
              {group.map((post, postIndex) => (
                <PostCol key={postIndex} post={post?.node} />
              ))}
            </div>
          </>
        );
      })}

      <Subscribe />
    </motion.div>
  );
}

export default BlogPosts;
